import React from 'react';
import { Helmet } from 'react-helmet';

export type SEOProps = {
  facebookDomainVerification?: string;
  appleTouch?: string;
  authorUrl?: string;
  defaultAuthorUrl?: string;
  defaultLocale?: string;
  defaultPublisherUrl?: string;
  defaultSiteImage?: string;
  description: string;
  favicon16?: string;
  favicon32?: string;
  globalSchema?: string;
  image?: string;
  noIndex?: boolean;
  keywords?: string | Array<string>;
  locale?: string;
  ogType?: string;
  pagePath: string;
  publisherUrl?: string;
  requireImage?: boolean;
  schema?: string;
  siteName?: string;
  siteUrl: string;
  title: string;
  twitterCard?: string;
  twitterCreator?: string;
  twitterSite?: string;
};

/**
 * SEO Component
 * Renders header meta tags for Search Engine Optimization!
 *
 * Originally we tried to implement this using gatsby-plugin-seo
 * Which is actually a plugin for gatsby that has a lot of duplicates.
 * The one we specifically tried is from here: https://github.com/franklintarter/gatsby-plugin-seo
 *
 * The problem is that it uses the gatsby useStaticQuery hook which we found to intermittently break during deployment.
 * See: https://github.com/gatsbyjs/gatsby/issues/26563
 *
 * For now we realized there was no reason to use a static query at all, it's just using it to query our global context, but we can just simplify this to use normal props.
 *
 * So this borrows that code as a influence, follows this doc: https://www.gatsbyjs.com/docs/add-seo-component/, but doesn't use the static query.
 *
 * @param {SEOProps} {
 *   appleTouch,
 *   authorUrl,
 *   defaultAuthorUrl,
 *   defaultLocale = "en_US",
 *   defaultPublisherUrl,
 *   defaultSiteImage = "src/images/corewell-health-logo.png",
 *   description,
 *   favicon16,
 *   favicon32,
 *   globalSchema,
 *   image,
 *   keywords,
 *   locale = "en_US",
 *   ogType = "website",
 *   pagePath,
 *   publisherUrl,
 *   requireImage = true,
 *   schema,
 *   siteName = "Corewell Health",
 *   siteUrl = "https://corewellhealth.org",
 *   title,
 *   twitterCard = "summary_large_image",
 *   twitterCreator = "@CorewellHealth",
 *   twitterSite = "@CorewellHealth"
 * }
 * @returns
 */

const SEO: React.FC<SEOProps> = ({
  appleTouch,
  authorUrl,
  defaultAuthorUrl,
  defaultLocale = 'en_US',
  defaultPublisherUrl,
  defaultSiteImage,
  description,
  facebookDomainVerification,
  favicon16,
  favicon32,
  globalSchema,
  image,
  noIndex,
  keywords,
  locale = 'en_US',
  ogType = 'website',
  pagePath,
  publisherUrl,
  requireImage = true,
  schema,
  siteName,
  siteUrl,
  title,
  twitterCard = 'summary_large_image',
  twitterCreator,
  twitterSite
}: SEOProps) => {
  /**
   * isValidJson is a helper util that checks if a string is valid json or something else
   */
  const isValidJson = (str: string): boolean => {
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }
    return true;
  };

  const pageLocale = locale || defaultLocale;
  const pageUrl = `${siteUrl}${pagePath}`;
  const pageImage = image || defaultSiteImage;
  const pageTitle = title;

  const pageAuthorUrl = authorUrl || defaultAuthorUrl;
  const pagePublisherUrl = publisherUrl || defaultPublisherUrl;

  let keywordsStr;
  if (keywords && typeof keywords === 'string') {
    keywordsStr = keywords.trim();
  }
  if (keywords && Array.isArray(keywords)) {
    keywordsStr = keywords.join(', ').trim();
  }

  const hasSchema = !!globalSchema || !!schema;

  let schemaJson;
  if (hasSchema) {
    schemaJson = `
    { "@context": "https://schema.org",
      "@graph": [
      ${globalSchema || ''}${globalSchema && schema ? ',' : ''}${schema || ''}
      ]
    }`;
    if (!isValidJson(schemaJson)) {
      throw Error("Invalid JSON Schema. Check the values of 'globalSchema' in gatsby-config.js, or 'schema' prop of the page");
    }
  }
  return (
    <Helmet>
      <html lang="en" />

      {/* Icons */}
      <link rel="apple-touch-icon" sizes="180x180" href={appleTouch} />
      <link rel="icon" type="image/png" href={favicon32} sizes="32x32" />
      <link rel="icon" type="image/png" href={favicon16} sizes="16x16" />
      <link rel="shortcut icon" href="/favicon.png" />

      {/* Core  */}
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      {keywordsStr && <meta name="keywords" content={keywordsStr} />}

      {/* For BHSH site search to find each term individually*/}
      {keywordsStr && keywordsStr.split(',').map((keyword, index) => <meta name="keyword" content={keyword} key={index} />)}

      {/* For Facebook domain verification */}
      {facebookDomainVerification && <meta name="facebook-domain-verification" content={facebookDomainVerification} />}

      {/* OG */}
      <meta property="og:url" content={pageUrl} />
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:locale" content={pageLocale} />
      <meta property="og:site_name" content={siteName} />

      {/* Images */}
      {requireImage && pageImage && <meta property="og:image" content={new URL(pageImage, siteUrl).toString()} />}
      {requireImage && pageImage && <meta property="og:image:secure_url" content={new URL(pageImage, siteUrl).toString()} />}

      {/* Article */}
      {ogType === 'article' && pagePublisherUrl && <meta property="article:publisher" content={pagePublisherUrl} />}
      {ogType === 'article' && pageAuthorUrl && <meta property="article:author" content={pageAuthorUrl} />}

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      {twitterSite && <meta name="twitter:site" content={twitterSite} />}
      {twitterCreator && <meta name="twitter:creator" content={twitterCreator} />}
      {hasSchema && (
        <script defer async type="application/ld+json">
          {schemaJson}
        </script>
      )}

      {/* Prevents most search engine web crawlers from indexing the page */}
      {noIndex && <meta name="robots" content="noindex" />}
    </Helmet>
  );
};

export default SEO;
